import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, ShieldCheck, Timer, Dumbbell } from "lucide-react";
import { api } from "@/api";
import { useLang } from "@/i18n";
import { ArticleCard } from "@/components/ArticleCard";
import Seo from "@/components/Seo";
import { NewsletterBox } from "@/components/NewsletterBox";
import { NewArticleBanner } from "@/components/NewArticleBanner";

const HERO = "https://images.unsplash.com/flagged/photo-1574005280900-3ff489fa1f70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxib3hpbmclMjBmaWdodGVyJTIwcmluZyUyMHRyYWluaW5nfGVufDB8fHx8MTc5MDMzMjc5MXww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { lang, t } = useLang();
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/articles", { params: { lang } }).then((r) => setArticles(r.data));
  }, [lang]);

  const featured = articles[0];
  const rest = articles.slice(1, 5);
  const reviews = articles.filter((a) => a.category === "Review").slice(0, 2);

  return (
    <div>
      <Seo title={t("hero_title")} description={t("hero_sub")} />
      <NewArticleBanner articles={articles} />
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0c0d10] via-[#0c0d10]/85 to-[#0c0d10]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0c0d10] to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-32 lg:py-40">
          <div className="max-w-2xl fade-up">
            <span className="text-xs font-mono uppercase tracking-[0.25em] text-[#E63946]">{t("hero_eyebrow")}</span>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-[1.05]">
              {t("hero_title")}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-xl leading-relaxed">{t("hero_sub")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/articles"
                data-testid="hero-read-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase tracking-wide rounded-md transition-colors"
              >
                {t("hero_cta_read")} <ArrowRight size={18} />
              </Link>
              <Link
                to="/blueprint"
                data-testid="hero-product-btn"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#3a3f4b] hover:border-white text-white font-bold uppercase tracking-wide rounded-md transition-colors"
              >
                {t("hero_cta_product")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* FEATURED + latest */}
        {featured && (
          <section className="py-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px flex-1 bg-[#272b35]" />
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">{t("featured")}</span>
              <span className="h-px flex-1 bg-[#272b35]" />
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <ArticleCard article={featured} size="lg" />
              <div className="grid sm:grid-cols-2 gap-6">
                {rest.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* PRODUCT teaser */}
        <section className="py-14">
          <div className="relative overflow-hidden rounded-2xl border border-[#272b35] bg-gradient-to-br from-[#14161b] to-[#0c0d10] p-8 sm:p-12">
            <span className="text-xs font-mono uppercase tracking-[0.25em] text-[#F59E0B]">{t("bp_eyebrow")}</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white max-w-2xl">
              {t("bp_title")}
            </h2>
            <p className="mt-4 text-zinc-300 max-w-2xl">{t("bp_sub")}</p>
            <div className="mt-8 grid sm:grid-cols-4 gap-4">
              {[
                { icon: Timer, label: lang === "it" ? "Round Timer" : "Round Timer" },
                { icon: Zap, label: lang === "it" ? "Combo Generator" : "Combo Generator" },
                { icon: ShieldCheck, label: lang === "it" ? "Checklist Tecnica" : "Technical Checklist" },
                { icon: Dumbbell, label: lang === "it" ? "Log Progressi" : "Progress Log" },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 bg-[#0c0d10]/60 border border-[#272b35] rounded-lg p-3">
                  <f.icon size={20} className="text-[#E63946]" />
                  <span className="text-sm font-semibold text-zinc-200">{f.label}</span>
                </div>
              ))}
            </div>
            <Link
              to="/blueprint"
              data-testid="home-blueprint-cta"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase tracking-wide rounded-md transition-colors"
            >
              {t("hero_cta_product")} <ArrowRight size={18} />
            </Link>
          </div>
        </section>

        {/* Reviews */}
        {reviews.length > 0 && (
          <section className="py-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold uppercase text-white mb-6">{t("reviews_title")}</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {reviews.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </section>
        )}

        {/* Newsletter */}
        <section className="py-14">
          <NewsletterBox variant="home" />
        </section>
      </div>
    </div>
  );
}
